/**
 * Base field definitions shared across all data grids
 * These are the common fields that appear in multiple grids
 * Each feature can extend or override these definitions
 */

export const BASE_FIELDS = {
  // Core identification fields
  id_: {
    id: 'id_',
    field: 'id_',
    headerName: 'ID',
    width: (theme) => theme ? parseInt(theme.spacing(8)) : 64,
    type: 'string',
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row?.id_ || row?.id
  },
  
  source_path: {
    id: 'source_path',
    field: 'source_path',
    patternField: 'source_path',
    headerName: 'Source Path',
    width: (theme) => theme ? parseInt(theme.spacing(25)) : 200,
    minWidth: 200,
    flex: 1,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    valueGetter: (value, row) => row?.source_path || row?.filename || `prep_${row?.id_ || row?.id}`
  },
  
  // Project hierarchy fields
  project: {
    id: 'project',
    field: 'project',
    patternField: 'project',
    apiField: 'project',
    headerName: 'Project',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Project name'
  },
  
  target: {
    id: 'target',
    field: 'target',
    patternField: 'target',
    apiField: 'target',
    headerName: 'Target',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 800,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Target protein'
  },
  
  mutation: {
    id: 'mutation',
    field: 'mutation',
    patternField: 'mutation',
    apiField: 'mutation',
    headerName: 'Mutation',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Mutation identifier'
  },
  
  subgroup: {
    id: 'subgroup',
    field: 'subgroup',
    patternField: 'subgroup',
    apiField: 'subgroup',
    headerName: 'Subgroup',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Subgroup identifier'
  },
  
  prep_workflow: {
    id: 'prep_workflow',
    field: 'prep_workflow',
    patternField: 'prep_workflow',
    apiField: 'prep_workflow',
    headerName: 'Workflow',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Preparation workflow used'
  },
  
  batch: {
    id: 'batch',
    field: 'batch',
    patternField: 'batch',
    apiField: 'batch',
    headerName: 'Batch',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    type: 'text',
    sortable: true,
    defaultVisible: true,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Batch identifier'
  },
  
  // Ligand fields
  ligand_name: {
    id: 'ligand_name',
    field: 'ligand_name',
    patternField: 'ligand',
    apiField: 'ligand_name',
    headerName: 'Ligand',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    sortable: true,
    defaultVisible: false,  // Will be overridden per grid
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Ligand/compound name'
  },
  
  ligand_tag: {
    id: 'ligand_tag',
    field: 'ligand_tag',
    patternField: 'ligand_tag',
    apiField: 'ligand_tag',
    headerName: 'Ligand Tag',
    width: (theme) => theme ? parseInt(theme.spacing(12)) : 96,
    type: 'text',
    sortable: true,
    defaultVisible: false,  // Will be overridden per grid
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Ligand tag/identifier'
  },
  
  // Metadata fields
  author: {
    id: 'author',
    field: 'author',
    patternField: 'author',
    apiField: 'author',
    headerName: 'Author',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    inSuggestions: true,
    description: 'Author/creator of the structure'
  },
  
  notes: {
    id: 'notes',
    field: 'notes',
    patternField: 'notes',
    apiField: 'notes',
    headerName: 'Notes',
    width: (theme) => theme ? parseInt(theme.spacing(20)) : 160,
    flex: 1,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    description: 'Additional notes'
  },
  
  tags: {
    id: 'tags',
    field: 'tags',
    patternField: 'tags',
    apiField: 'tags',
    headerName: 'Tags',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    editable: true,
    searchable: true,
    description: 'Tags for categorization'
  },
  
  // Date fields
  prepared_at: {
    id: 'prepared_at',
    field: 'prepared_at',
    patternField: 'prepared_at',
    apiField: 'prepared_at',
    headerName: 'Date Prepared',
    width: (theme) => theme ? parseInt(theme.spacing(14)) : 112,
    type: 'date',
    sortable: true,
    defaultVisible: false,
    editable: true,
    valueGetter: (value) => {
      if (!value) return null;
      const date = new Date(value);
      return date.toLocaleDateString();
    }
  },
  
  created_at: {
    id: 'created_at',
    field: 'created_at',
    apiField: 'created_at',
    headerName: 'Created',
    width: (theme) => theme ? parseInt(theme.spacing(18)) : 144,
    type: 'dateTime',
    sortable: true,
    defaultVisible: false,
    editable: false,
    valueGetter: (value) => {
      if (!value) return null;
      const date = new Date(value);
      return date.toLocaleString();
    }
  },
  
  updated_at: {
    id: 'updated_at',
    field: 'updated_at',
    apiField: 'updated_at',
    headerName: 'Updated',
    width: (theme) => theme ? parseInt(theme.spacing(14)) : 112,
    type: 'dateTime',
    sortable: true,
    defaultVisible: false,
    editable: false,
    valueGetter: (value) => {
      if (!value) return null;
      const date = new Date(value);
      return date.toLocaleString();
    }
  }
};

// Export field IDs for easy reference
export const FIELD_IDS = Object.keys(BASE_FIELDS);

// Export function to get field by ID
export const getBaseField = (fieldId) => BASE_FIELDS[fieldId];

// Export function to get multiple fields
export const getBaseFields = (fieldIds) => {
  return fieldIds.map(id => BASE_FIELDS[id]).filter(Boolean);
};